// ═══════════════════════════════════════════════════════════════
// HOOK: useDepartments - Con función de refresh
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../../../contexts/CompanyContext';
import api from '../../../api/config';

export default function useDepartments() {
  const { currentCompany } = useCompany();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para cargar departamentos
  const loadDepartments = useCallback(async () => {
    if (!currentCompany?.id) {
      console.log('⚠️ No hay compañía seleccionada');
      setDepartments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('📥 Cargando departamentos...');
      const response = await api.get('/departments');
      
      console.log('✅ Departamentos cargados:', response.data.length);
      setDepartments(response.data || []);
    } catch (error) {
      console.error('❌ Error cargando departamentos:', error);
      setError(error.message);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  // Cargar al montar y cuando cambie la compañía
  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  // Función para refrescar (expuesta para uso externo)
  const refresh = useCallback(async () => {
    console.log('🔄 Refrescando departamentos...');
    await loadDepartments();
  }, [loadDepartments]);

  // Agregar departamento
  const addDepartment = async (departmentData) => {
    try {
      console.log('➕ Creando departamento:', departmentData);
      const response = await api.post('/departments', departmentData);
      
      console.log('✅ Departamento creado:', response.data);
      
      // Refrescar lista
      await refresh();
      
      return response.data;
    } catch (error) {
      console.error('❌ Error creando departamento:', error);
      throw error;
    }
  };

  // Actualizar departamento
  const updateDepartment = async (id, departmentData) => {
    try {
      console.log('✏️ Actualizando departamento:', id, departmentData);
      const response = await api.put(`/departments/${id}`, departmentData);
      
      console.log('✅ Departamento actualizado:', response.data);
      
      // Refrescar lista
      await refresh();
      
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando departamento:', error);
      throw error;
    }
  };

  // Eliminar departamento
  const deleteDepartment = async (id) => {
    try {
      console.log('🗑️ Eliminando departamento:', id);
      await api.delete(`/departments/${id}`);
      
      console.log('✅ Departamento eliminado');
      
      // Refrescar lista
      await refresh();
    } catch (error) {
      console.error('❌ Error eliminando departamento:', error);
      
      if (error.response?.data?.error) {
        alert(`❌ ${error.response.data.error}`);
      } else {
        alert('❌ Error al eliminar el departamento');
      }
      
      throw error;
    }
  };

  return {
    departments,
    loading,
    error,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    refresh // ← Exponer función de refresh
  };
}
